import { useRef } from "react";
import axios from "axios";
import { recordException } from "components/observability/observability";

export const useRequestOptions = () => {
    const requestInterceptor = useRef<number>();
    const responseInterceptor = useRef<number>();

    // if we have an existing interceptor, remove it before adding the new one
    if (requestInterceptor.current !== undefined) {
        axios.interceptors.request.eject(requestInterceptor.current);
    }

    if (responseInterceptor.current !== undefined) {
        axios.interceptors.response.eject(responseInterceptor.current);
    }

    // add authorization token to each request
    requestInterceptor.current = axios.interceptors.request.use(
        async function (config) {
            config.withCredentials = true;

            // Try to get aws waf token
            try {
                const token = await window.AwsWafIntegration.getToken();
                config.headers.set("x-aws-waf-token", token);
            } catch (error) {
                console.info("Unable to get AWS WAF token");
            }

            // Add other headers that you need here
            return config;
        },
        function (error) {
            return Promise.reject(new Error(error));
        },
    );

    // Add response interceptor to catch all HTTP errors globally
    responseInterceptor.current = axios.interceptors.response.use(
        function (response) {
            // Any status code in 2xx range triggers this
            return response;
        },
        function (error) {
            // Any status code outside 2xx range triggers this
            const method = error.config?.method;
            const endpoint = error.config?.url;
            const status = error.response?.status;

            recordException(error, {
                type: 'API_ERROR',
                isApiError: true,
                status,
                endpoint,
                method,
            }, {
                method,
                url: endpoint,
                statusCode: status,
            });
            return Promise.reject(error);
        }
    );
};

export default useRequestOptions;